import clsx from "clsx";
import Link from "next/link";

import { AppShell } from "@chef/feature-app-shell";
import { useExtendedBasketData } from "@chef/state-management/hooks";
import {
  CurrencyWrapper as Currency,
  PageLoader,
} from "@chef/smart-components";
import { getBasketDifference } from "@chef/state-management/helpers";
import { useIntercom } from "@chef/hooks";
import { isEmptyArray, isNotEmptyArray, sumByFn } from "@chef/utils/array";
import {
  isGroceryProduct,
  isMealboxProduct,
  isPickAndMixProduct,
} from "@chef/helpers";
import { ISection } from "@chef/feature-sanity";
import { Button, Card, Snackbar } from "@chef/components";
import { BRAND_PATHS } from "@chef/constants";

import type { WeekYear } from "@chef/types/common";

import { Summary } from "../lib/Summary";
import { WeekStatus } from "../lib/WeekStatus";
import { EditModeButton } from "../lib/EditModeButton";
import { PickAndMixBasketList } from "../lib/tiles/PickAndMixBasketList";
import { StandaloneProductList } from "../lib/tiles/StandaloneProductBasketList";

import { intl } from "./WeekEditorConfirmOrderExperimental.Intl";

interface WeekEditorConfirmOrderExperimentalProps extends WeekYear {
  whyDeliveryChangeSection: ISection | null;
}

interface BelowMinMealsWarningProps {
  isVisible: boolean;
  className?: string;
  minMeals: number;
}

const BelowMinMealsWarning = ({
  isVisible,
  className,
  minMeals,
}: BelowMinMealsWarningProps) => {
  if (!isVisible) {
    return null;
  }

  return (
    <Snackbar error className={clsx("mb-6 -mt-3", className)}>
      {intl.YOU_MUST_HAVE_MINIMUM_MEALS_TO_RECEIVE_DELIVERY(minMeals)}
    </Snackbar>
  );
};

export const WeekEditorConfirmOrderExperimental = ({
  week,
  year,
  whyDeliveryChangeSection,
}: WeekEditorConfirmOrderExperimentalProps) => {
  const { data: extendedBasket } = useExtendedBasketData({
    week,
    year,
  });

  useIntercom(false);

  const {
    basketHasDeviations,
    selectedProducts = [],
    chosenProducts,
  } = extendedBasket;

  const basketDifference = getBasketDifference(
    chosenProducts,
    selectedProducts,
  );
  const basketCostDifference = sumByFn(
    basketDifference,
    (product) => product.price * product.quantity,
  );

  const pickAndMixProducts = selectedProducts.filter(isPickAndMixProduct);
  const groceryProducts = selectedProducts.filter(isGroceryProduct);

  const hasStandaloneProducts = isNotEmptyArray(groceryProducts);

  const hasPickAndMixProducts = isNotEmptyArray(pickAndMixProducts);

  const isBelowMinMeals = pickAndMixProducts.length < extendedBasket.minMeals;

  const showCartProgressButton = basketHasDeviations && !isBelowMinMeals;

  if (isEmptyArray(chosenProducts)) {
    return <PageLoader />;
  }

  const hasProductsInBasket =
    selectedProducts.filter((product) => !isMealboxProduct(product)).length > 0;

  return (
    <AppShell title={intl.ORDER}>
      <AppShell.Meta />

      <AppShell.Drawer />

      <AppShell.Sticky anchor="top" float>
        <AppShell.Header variant="my-menu" />
      </AppShell.Sticky>

      <WeekStatus
        week={week}
        year={year}
        sanityData={whyDeliveryChangeSection}
      />

      <AppShell.Main grid>
        <h1 className="hidden mb-6 md:block md:col-span-full">
          <strong>{intl.ORDER}</strong>
        </h1>
        <div className="col-start-1 col-span-full lg:col-span-5 xl:col-span-8">
          <BelowMinMealsWarning
            isVisible={isBelowMinMeals}
            className="lg:hidden"
            minMeals={extendedBasket.minMeals}
          />

          <section className="col-span-full lg:col-span-5 xl:col-span-9">
            {hasPickAndMixProducts && (
              <>
                <h2 className="mb-2 text-lg capitalize">
                  <strong>{intl.MEALS}</strong>
                </h2>

                <PickAndMixBasketList week={week} year={year} />
              </>
            )}

            {hasStandaloneProducts && (
              <>
                <h2 className="mt-6 mb-2 text-lg capitalize">
                  <strong>{intl.ADDONS}</strong>
                </h2>

                <StandaloneProductList week={week} year={year} />
              </>
            )}
          </section>
        </div>
        <section
          className={clsx(
            "gap-4 col-span-full lg:col-span-3 xl:col-span-4",
            basketHasDeviations && "mb-20",
          )}
        >
          <BelowMinMealsWarning
            isVisible={isBelowMinMeals}
            className="hidden lg:flex"
            minMeals={extendedBasket.minMeals}
          />
          <Card
            className={clsx(
              "md:top-60 md:sticky text-grey-1 md:text-black",
              !hasProductsInBasket && "!bg-background !shadow-none",
            )}
          >
            {hasProductsInBasket && <Summary week={week} year={year} />}
            {showCartProgressButton && (
              <div className="fixed z-20 w-full px-4 pb-4 mb-4 -translate-x-1/2 md:p-0 md:relative md:translate-x-0 bottom-18 md:bottom-0 left-1/2 md:left-0">
                <Link
                  href={BRAND_PATHS.WEEK_EDITOR_ADD_MORE_PAGE({ week, year })}
                  passHref
                >
                  <Button disabled={isBelowMinMeals} primary full>
                    <div className="flex justify-between">
                      <span>{intl.CONFIRM_CHANGES}</span>

                      {basketHasDeviations && (
                        <Currency
                          trimDecimal
                          prefix={basketCostDifference > 0 ? "+ " : "- "}
                        >
                          {basketCostDifference}
                        </Currency>
                      )}
                    </div>
                  </Button>
                </Link>
              </div>
            )}
            {basketHasDeviations && (
              <EditModeButton week={week} year={year} revertOnly full />
            )}
          </Card>
        </section>
      </AppShell.Main>
      <AppShell.Sticky anchor="bottom">
        <AppShell.TabBar variant="my-menu" />
      </AppShell.Sticky>
    </AppShell>
  );
};
